/**
 * JSON Schema definitions for provider-native structured output.
 *
 * These schemas are passed to LLM providers that support constrained
 * generation (Gemini responseSchema, OpenAI json_schema, Claude tool_use).
 * They eliminate JSON parse failures by constraining token generation
 * at inference time.
 *
 * IMPORTANT: These are plain JSON Schema objects, not Zod schemas.
 * No additional dependencies required.
 */

/** Schema for a single modifier in a mechanics roll. */
const MODIFIER_SCHEMA = {
  type: "object" as const,
  properties: {
    source: { type: "string" as const },
    value: { type: "number" as const },
    reason: { type: "string" as const },
  },
  required: ["source", "value", "reason"] as const,
  additionalProperties: false,
};

/** Schema for mechanics agent structured output. */
export const MECHANICS_RESULT_SCHEMA = {
  type: "object" as const,
  properties: {
    rollNeeded: { type: "boolean" as const },
    skill: { type: "string" as const },
    targetNumber: { type: "number" as const },
    modifiers: {
      type: "array" as const,
      items: MODIFIER_SCHEMA,
    },
    difficulty: {
      type: "string" as const,
      enum: ["trivial", "easy", "moderate", "hard", "extreme"] as const,
    },
    timeEstimate: { type: "string" as const },
  },
  required: ["rollNeeded", "skill"] as const,
  additionalProperties: false,
};

/** Schema for character assignment (GURPS stat block). */
export const CHARACTER_ASSIGNMENT_SCHEMA = {
  type: "object" as const,
  properties: {
    name: { type: "string" as const },
    st: { type: "number" as const },
    dx: { type: "number" as const },
    iq: { type: "number" as const },
    ht: { type: "number" as const },
    skills: {
      type: "object" as const,
      additionalProperties: { type: "number" as const },
    },
    rationale: { type: "string" as const },
  },
  required: ["name", "st", "dx", "iq", "ht", "skills", "rationale"] as const,
  additionalProperties: false,
};

/** Schema for a single background option. */
const BACKGROUND_OPTION_SCHEMA = {
  type: "object" as const,
  properties: {
    name: { type: "string" as const },
    description: { type: "string" as const },
  },
  required: ["name", "description"] as const,
  additionalProperties: false,
};

/** Schema for background generation (array of 5 options). */
export const BACKGROUNDS_SCHEMA = {
  type: "array" as const,
  items: BACKGROUND_OPTION_SCHEMA,
  minItems: 5,
  maxItems: 5,
};

/** Schema for a single player option. */
const OPTION_ITEM_SCHEMA = {
  type: "object" as const,
  properties: {
    label: { type: "string" as const },
    text: { type: "string" as const },
    category: {
      type: "string" as const,
      enum: ["wildcard", "cautious", "aggressive", "creative"] as const,
    },
  },
  required: ["label", "text", "category"] as const,
  additionalProperties: false,
};

/** Schema for option generation (array of 4 options). */
export const OPTIONS_SCHEMA = {
  type: "array" as const,
  items: OPTION_ITEM_SCHEMA,
  minItems: 4,
  maxItems: 4,
};
